"use client";

import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  SortOption,
  SORT_OPTIONS,
  SORT_LABELS,
  type SortDirection,
} from "@/util/contributions";
import { ContributionType } from "@/data/contribution-types";
import { ArrowUp, ArrowDown } from "lucide-react";

type ContributionsControlsProps = {
  query: string;
  sort: SortOption;
  sortDirection: SortDirection;
  typeFilter: ContributionType | null;
  techFilter: string | null;
  technologies: string[];
  availableTypes: ContributionType[];
  onSearchChange: (value: string) => void;
  onSortChange: (value: SortOption) => void;
  onSortDirectionToggle: () => void;
  onTypeFilterChange: (value: ContributionType | null) => void;
  onTechFilterChange: (value: string | null) => void;
  resultCount: {
    filteredProjects: number;
    totalProjects: number;
    visibleCommits: number;
    isFiltered: boolean;
  };
};

export function ContributionsControls({
  query,
  sort,
  sortDirection,
  typeFilter,
  techFilter,
  technologies,
  availableTypes,
  onSearchChange,
  onSortChange,
  onSortDirectionToggle,
  onTypeFilterChange,
  onTechFilterChange,
  resultCount,
}: ContributionsControlsProps) {
  const handleSortChange = (value: string) => {
    if (SORT_OPTIONS.includes(value as SortOption)) {
      onSortChange(value as SortOption);
    }
  };

  const handleTypeFilterChange = (value: string) => {
    if (value === "all") {
      onTypeFilterChange(null);
    } else if (availableTypes.includes(value as ContributionType)) {
      onTypeFilterChange(value as ContributionType);
    }
  };

  const handleTechFilterChange = (value: string) => {
    if (value === "all") {
      onTechFilterChange(null);
    } else if (technologies.includes(value)) {
      onTechFilterChange(value);
    }
  };

  const SortIcon = sortDirection === "asc" ? ArrowUp : ArrowDown;

  return (
    <div className="flex flex-col gap-4 min-[720px]:flex-row min-[720px]:flex-wrap min-[720px]:items-center">
      {/* Results count - left aligned */}
      <p className="text-muted-foreground text-base whitespace-nowrap">
        {resultCount.isFiltered ? (
          <>
            Showing {resultCount.filteredProjects} of{" "}
            {resultCount.totalProjects} projects ({resultCount.visibleCommits}{" "}
            commits)
          </>
        ) : (
          <>
            {resultCount.filteredProjects} projects •{" "}
            {resultCount.visibleCommits} commits
          </>
        )}
      </p>

      {/* Controls - right aligned on desktop, grows to fill available space between 720-1024px */}
      <div className="flex flex-col gap-2 min-[720px]:flex-1 min-[720px]:flex-row min-[720px]:flex-wrap min-[720px]:items-center min-[720px]:gap-2 lg:ml-auto lg:flex-none">
        <SearchInput
          id="contributions-search"
          containerClassName="w-full min-[720px]:min-w-44 min-[720px]:flex-1 lg:w-96 lg:flex-none"
          value={query}
          onValueChange={onSearchChange}
          placeholder="Search..."
          data-testid="contributions-search"
          aria-label="Search contributions"
        />
        <Select
          value={typeFilter ?? "all"}
          onValueChange={handleTypeFilterChange}
        >
          <SelectTrigger
            label="Filter by contribution type"
            data-testid="contributions-type-filter"
            className="w-full min-[720px]:w-auto"
          >
            {typeFilter ?? "All types"}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {availableTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={techFilter ?? "all"}
          onValueChange={handleTechFilterChange}
        >
          <SelectTrigger
            label="Filter by technology"
            data-testid="contributions-tech-filter"
            className="w-full min-[720px]:w-auto"
          >
            {techFilter ?? "All tech"}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tech</SelectItem>
            {technologies.map((tech) => (
              <SelectItem key={tech} value={tech}>
                {tech}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Select value={sort} onValueChange={handleSortChange}>
            <SelectTrigger
              label="Sort contributions by"
              data-testid="contributions-sort"
              className="w-full min-[720px]:w-auto"
            >
              <span className="flex items-center gap-1.5">
                {SORT_LABELS[sort]}
              </span>
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option} value={option}>
                  {SORT_LABELS[option]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={onSortDirectionToggle}
            aria-label={
              sortDirection === "asc" ? "Sort descending" : "Sort ascending"
            }
            title={
              sortDirection === "asc" ? "Sort descending" : "Sort ascending"
            }
            className="shrink-0"
          >
            <SortIcon className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
